import React, { forwardRef, useImperativeHandle } from "react";
import { Decorator, Input } from "@myhealth-raccoon/components";
import { validation } from "@myhealth-raccoon/forms";
import { HelpCircle } from "@untitled-ui/icons-react";
import classNames from "classnames";
import { useForm } from "react-hook-form";

import { PatientUpdateMedicare } from "src/services/patient";
import { useSelector } from "src/store";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";

type Props = {
    shouldShowValidation?: boolean;
    className?: string;
    prepend?: React.ReactNode;
    append?: React.ReactNode;
}

type Form = PatientUpdateMedicare & {
    haveMedicare: "yes" | "no"
}

export interface MedicareFormRef {
    submit(): Promise<Form>;
}

export const MedicareForm = forwardRef<MedicareFormRef, Props>(({ shouldShowValidation, className, prepend, append }, ref) => {

    const isNewPatient = useSelector(state => state.appointment.isNewPatient);
    const { register, watch, formState: { errors }, handleSubmit } = useForm<Form>({
        defaultValues: {
            haveMedicare: isNewPatient ? undefined : "no"
        }
    });

    const medicareRadio = register("haveMedicare", { validate: validation.required() });

    useImperativeHandle(ref, () => ({
        submit: async () => {
            return new Promise((resolve, reject) => {
                handleSubmit(resolve, reject)()
            })
        }
    }));

    return <>
        {prepend}
        <form className={classNames("space-y-2", className)}>
            <Decorator fieldset label={<>
                <div className="flex flex-row gap-2">
                    Do you have a Medicare card?

                    <Popover className="relative">
                        <PopoverButton>
                            <HelpCircle className="size-5 text-gray-400" />
                        </PopoverButton>
                        <PopoverPanel anchor="bottom" className="shadow-lg [--anchor-gap:4px] py-2 px-3 bg-gray-900 rounded-lg">
                            <img src="/medicarecardhelp.png" />
                        </PopoverPanel>
                    </Popover>
                </div></>}
                fieldError={shouldShowValidation ? errors.haveMedicare : undefined}>
                <div className="flex flex-row gap-5 text-base py-1.5">
                    <Decorator
                        className="flex flex-row gap-3">
                        <input
                            type="radio"
                            className="radio align-bottom text-xl mt-0.5"
                            value="yes" {...medicareRadio} id="haveMedicareTrue"
                        />
                        <label htmlFor="haveMedicareTrue" className="text-gray-700 cursor-pointer">Yes</label>
                    </Decorator>
                    <Decorator
                        className="flex flex-row gap-3">
                        <input
                            type="radio"
                            className="radio align-bottom text-xl mt-0.5"
                            value="no" {...medicareRadio} id="haveMedicareFalse"
                        />
                        <label htmlFor="haveMedicareFalse" className="text-gray-700 cursor-pointer">No</label>
                    </Decorator>
                </div>
            </Decorator>
            {watch('haveMedicare') === "yes" && <div className="mt-5 rounded-xl py-5 px-4 gap-5 flex flex-col bg-gray-50 border">
                <Input
                    {...register("number", { validate: validation.composeValidators(validation.required()) })}
                    type="text"
                    inputMode="numeric"
                    label="Medicare number"
                    fieldError={shouldShowValidation ? errors.number : undefined}
                />
                <div className="flex flex-row gap-2">
                    <Input
                        className="flex-1"
                        {...register("irn", { validate: validation.composeValidators(validation.min(0), validation.maxLength(1)) })}
                        type="number"
                        inputMode="numeric"
                        label="IRN"
                        fieldError={shouldShowValidation ? errors.irn : undefined}
                    />
                    <Input
                        className="flex-1"
                        {...register("expiry")}
                        type="month"
                        label="Expiry"
                        fieldError={shouldShowValidation ? errors.expiry : undefined}
                    />
                </div>
            </div>}
        </form>
        {append}
    </>
})